import React, { useState, useCallback, useContext, useMemo } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import {
  useKeywordPlannerService,
  CHANNEL_TYPES,
} from '@bingads-webui-campaign-react/keyword-planner-shared';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { SearchInputStack } from './search-input-stack';
import { useKeywordSuggestion, useSearchBoxStatus, getSearchIdeaModel } from './util/index';

export const SearchInputStackContainer = ({
  targetedLocationInfo,
  supportedLanguages,
  beforeSearch,
  onSearchSuccess,
  onSearchFinally,
}) => {
  const activity = useContext(ActivityContext);
  const [url, setUrl] = useState('');
  const [keywords, setKeywords] = useState('');
  const recommendateRequest = useKeywordSuggestion();
  const {
    Permissions,
    DynamicPermissions,
  } = useContext(ServerContext);
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const inputStatus = useSearchBoxStatus(targetedLocationInfo, supportedLanguages, permissions);
  const { getSearchIdea } = useKeywordPlannerService();
  const onSearch = useCallback(() => {
    if (!_.isEmpty(url) || !_.isEmpty(keywords)) {
      beforeSearch();
      // eslint-disable-next-line no-unused-expressions
      activity && activity.signal(`onSearchSidePanelKeywordsSuggestion - ${JSON.stringify({
        Url: url,
        Keywords: keywords,
      })}`);
      if (permissions && permissions.dynamic && permissions.dynamic.KeywordPlannerLiteRefactorPhase1) {
        getSearchIdea({
          model: getSearchIdeaModel({
            keywords,
            url,
            targetedLocationInfo,
            languages: supportedLanguages,
          }),
          isLanguageDecoupling: true,
          channel: CHANNEL_TYPES.CAMPAIGN_CREATION,
        })
          .then(data => onSearchSuccess(data))
          .catch((error) => {
            // eslint-disable-next-line no-unused-expressions
            activity && activity.signal(`onFetchRecommendationInSetting - has error - ${JSON.stringify({
              ErrorStatus: error && error.status,
            })}`);
          })
          .finally(onSearchFinally);
      } else {
        recommendateRequest({
          keywords,
          url,
          targetedLocationInfo,
          languages: supportedLanguages,
          activity,
        })
          .then(data => onSearchSuccess({ data }))
          .catch((error) => {
            // eslint-disable-next-line no-unused-expressions
            activity && activity.signal(`onFetchRecommendationInSetting - has error - ${JSON.stringify({
              ErrorStatus: error && error.status,
            })}`);
          })
          .finally(onSearchFinally);
      }
    }
  }, [
    beforeSearch,
    keywords,
    onSearchFinally,
    onSearchSuccess,
    recommendateRequest,
    supportedLanguages,
    targetedLocationInfo,
    url,
    activity,
    getSearchIdea,
    permissions,
  ]);

  const onUrlChange = useCallback((e, value) => {
    setUrl(value === undefined ? '' : value);
  }, []);
  const onKeywordsChange = useCallback((e, value) => {
    setKeywords(value === undefined ? '' : value);
  }, []);

  return (
    <SearchInputStack
      url={url}
      keywords={keywords}
      inputStatus={inputStatus}
      onUrlChange={onUrlChange}
      onKeywordsChange={onKeywordsChange}
      onSearch={onSearch}
    />
  );
};

SearchInputStackContainer.propTypes = {
  beforeSearch: PropTypes.func.isRequired,
  onSearchSuccess: PropTypes.func.isRequired,
  onSearchFinally: PropTypes.func.isRequired,
  targetedLocationInfo: PropTypes.shape({}).isRequired,
  supportedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
};
